<template>
  <main class="w-full">
    <header class="mb-2 py-2 px-6 bg-white sticky top-0">
      <h1 class="text-3xl mb-2 font-bold text-gray-800">Marques de produit</h1>
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="relative flex-1">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            placeholder="Rechercher une marque..."
            class="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :value="searchTerm"
            @input="searchTerm = $event.target.value"
          />
        </div>
        <button
          @click="openAddModal"
          class="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md transition"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Nouvelle marque
        </button>
      </div>
    </header>
    
    <div class="p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <p class="text-gray-600">Chargement des marques...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredbrands.length === 0 && !loading" class="bg-white rounded-xl shadow border border-gray-200">
        <div class="p-12 text-center">
          <svg class="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">
            {{ searchTerm ? 'Aucun résultat' : 'Aucune marque' }}
          </h3>
          <p class="text-gray-600 mb-6">
            {{ searchTerm ? 'Aucune marque ne correspond à votre recherche' : 'Commencez par créer votre première marque' }}
          </p>
          <button
            @click="openAddModal"
            class="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md transition mx-auto"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nouvelle marque
          </button>
        </div>
      </div>

      <!-- Brands Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="brand in filteredbrands"
          :key="brand.id"
          class="bg-white rounded-xl shadow border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div class="p-5">
            <div class="flex items-start justify-between mb-3">
              <div class="flex items-center space-x-3">
                <div v-if="brand.logo" class="w-10 h-10">
                  <img 
                    :src="`${$store.state.productbrand}/${brand.logo}`" 
                    :alt="brand.name"
                    class="w-full h-full object-contain"
                  />
                </div>
                <div v-else class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800">{{ brand.name }}</h3>
                  <p v-if="brand.slug" class="text-xs text-gray-500">/{{ brand.slug }}</p>
                </div>
              </div>
              <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(brand.is_active)}`">
                {{ getStatusLabel(brand.is_active) }}
              </span>
            </div>

            <p v-if="brand.description" class="text-sm text-gray-600 mb-3 line-clamp-2">
              {{ brand.description }}
            </p>

            <div class="flex items-center justify-between pt-3 border-t border-gray-200">
              <div class="flex space-x-2">
                <button
                  @click="handleEdit(brand)"
                  class="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
                >
                  <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                  Modifier
                </button>
                <button
                  @click="showDeleteModal = { id: brand.id, name: brand.name }"
                  class="p-2 border border-gray-300 rounded-lg hover:bg-red-50 transition"
                >
                  <svg class="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Brand Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click.self="resetForm"
      >
        <div class="bg-white rounded-xl shadow-lg w-full max-w-md">
          <div class="p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-semibold text-gray-800">
                {{ editingbrand ? 'Modifier la marque' : 'Nouvelle marque' }}
              </h3>
              <button
                @click="resetForm"
                class="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form @submit.prevent="handleSubmit" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la marque *
                </label>
                <input
                  v-model="brandform.name"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Slug *
                </label>
                <input
                  v-model="brandform.slug"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  v-model="brandform.is_active"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option :value="1">Active</option>
                  <option :value="0">Désactivée</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  v-model="brandform.description"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Logo (optionnel)
                </label>
                <div class="flex items-center space-x-4">
                  <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileChange"
                    accept="image/*"
                    class="hidden"
                  />
                  <button
                    type="button"
                    @click="$refs.fileInput.click()"
                    class="border border-gray-300 bg-white hover:bg-gray-50 py-2 px-4 rounded-lg transition"
                  >
                    {{ brandform.logoFile ? 'Changer' : 'Choisir un logo' }}
                  </button>
                  <span v-if="brandform.logoFile" class="text-sm text-gray-500">
                    {{ brandform.logoFile.name }}
                  </span>
                  <span v-else-if="brandform.logo" class="text-sm text-gray-500">
                    Logo actuel
                  </span>
                </div>
                
                <!-- Preview du logo -->
                <div v-if="brandform.logo && !brandform.logoFile" class="mt-2">
                  <img 
                    :src="`${$store.state.productbrand}/${brandform.logo}`" 
                    :alt="brandform.name"
                    class="w-16 h-16 object-contain border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              <div v-if="error" class="bg-red-50 text-red-800 p-3 rounded-lg text-sm">
                {{ error }}
              </div>

              <div class="flex space-x-3 pt-4">
                <button
                  type="button"
                  @click="resetForm"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="saving" class="flex items-center justify-center">
                    <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    {{ editingbrand ? 'Enregistrement...' : 'Ajout...' }}
                  </span>
                  <span v-else>
                    {{ editingbrand ? 'Enregistrer' : 'Ajouter' }}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Delete Modal -->
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click.self="showDeleteModal = null"
      >
        <div class="bg-white rounded-xl shadow-lg w-full max-w-sm">
          <div class="p-6 text-center">
            <svg class="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              Confirmer la suppression
            </h3>
            <p class="text-gray-600 mb-6">
              Supprimer "{{ showDeleteModal.name }}" ?
            </p>
            <div v-if="deleteError" class="bg-red-50 text-red-800 p-3 rounded-lg text-sm mb-4">
              {{ deleteError }}
            </div>
            <div class="flex space-x-3">
              <button
                @click="showDeleteModal = null"
                :disabled="deleting"
                class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                @click="handleDelete"
                :disabled="deleting"
                class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="deleting" class="flex items-center justify-center">
                  <svg class="animate-spin h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Suppression...
                </span>
                <span v-else>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'BrandsPage',

  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      showModal: false,
      showDeleteModal: null,
      editingbrand: null,
      searchTerm: '',
      error: null,
      deleteError: null,

      brandform: {
        id: null,
        name: '',
        slug: '',
        description: '',
        is_active: 1,
        logo: null,
        logoFile: null
      }
    }
  },

  computed: {
    // Utilisez les marques admin du store
    brands() {
      return this.$store.state.admin.brands
    },
    
    // Ou utilisez le getter
    adminBrands() {
      return this.$store.getters.adminBrands
    },
    
    filteredbrands() {
      const brandsList = this.brands
      if (!brandsList) return []
      
      return brandsList.filter(brand => 
        brand.name && brand.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (brand.slug && brand.slug.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (brand.description && brand.description.toLowerCase().includes(this.searchTerm.toLowerCase()))
      )
    }
  },

  async mounted() {
    await this.loadBrands()
  },

  methods: {
    async loadBrands() {
      try {
        this.loading = true
        // Chargez les données admin depuis le store
        await this.$store.dispatch('fetchAdminData')
      } catch (error) {
        console.error('Erreur lors du chargement des marques:', error)
        this.error = 'Impossible de charger les marques. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },

    getStatusColor(isActive) {
      return isActive == 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
    },

    getStatusLabel(isActive) {
      return isActive == 1 ? 'Active' : 'Inactive'
    },

    openAddModal() {
      this.editingbrand = null
      this.resetFormData()
      this.showModal = true
      this.error = null
    },

    resetFormData() {
      this.brandform = {
        id: null,
        name: '',
        slug: '',
        description: '',
        is_active: 1,
        logo: null,
        logoFile: null
      }
    },

    resetForm() {
      this.showModal = false
      this.editingbrand = null
      this.resetFormData()
      this.error = null
    },

    async handleSubmit() {
      try {
        this.saving = true
        this.error = null
        
        // Générer le slug si vide
        if (!this.brandform.slug && this.brandform.name) {
          this.brandform.slug = this.generateSlug(this.brandform.name)
        }

        // Préparez les données pour l'API
        const formData = new FormData()
        
        // Ajoutez tous les champs sauf les fichiers
        Object.keys(this.brandform).forEach(key => {
          if (key !== 'logoFile') {
            if (this.brandform[key] !== null && this.brandform[key] !== undefined) {
              formData.append(key, this.brandform[key])
            }
          }
        })
        
        // Ajoutez le fichier logo s'il y en a un
        if (this.brandform.logoFile instanceof File) {
          formData.append('logo', this.brandform.logoFile)
        }
        
        // Appelez l'action du store
        const savedBrand = await this.$store.dispatch('saveBrand', formData)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: `Marque ${this.editingbrand ? 'modifiée' : 'créée'} avec succès`,
          type: 'success'
        })
        
        this.resetForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
        this.error = error.response?.data?.message || 'Une erreur est survenue lors de la sauvegarde.'
      } finally {
        this.saving = false
      }
    },

    handleEdit(brand) {
      this.editingbrand = brand
      this.brandform = {
        id: brand.id,
        name: brand.name || '',
        slug: brand.slug || '',
        description: brand.description || '',
        is_active: brand.is_active !== undefined ? brand.is_active : 1,
        logo: brand.logo || null,
        logoFile: null
      }
      this.showModal = true
      this.error = null
    },

    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.brandform.logoFile = file
      }
    },
    notify(title,message,type){
          this.$emit('notify',{title:title,message:message,type:type})
    },

    async handleDelete() {
      try {
        this.deleting = true
        this.deleteError = null
        
        // Appelez l'action de suppression du store
        await this.$store.dispatch('deleteBrand', this.showDeleteModal.id)
        
        // Affichez un message de succès
        this.notify({
          title: 'Succès',
          text: 'Marque supprimée avec succès',
          type: 'success'
        })
        
        this.showDeleteModal = null
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        this.deleteError = error.response?.data?.message || 'Une erreur est survenue lors de la suppression.'
      } finally {
        this.deleting = false
      }
    },

    generateSlug(name) {
  return name
    .toLowerCase()
    // Autorise lettres (avec accents), chiffres et tirets
    .replace(/[^a-z0-9àâäéèêëîïôöùûüç-]+/gi, '-')
    // Regroupe tous les tirets multiples en un seul
    .replace(/-+/g, '-')
    // Supprime les tirets au début et à la fin
    .replace(/^-+|-+$/g, '');
}
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

input, select {
  transition: all 0.2s ease;
}

input:focus, select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.fixed {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.bg-white {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bg-white:hover {
  transform: translateY(-2px);
}
</style>